import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axiosInstance from "../../globalInspector/axiosInstance";
import { env } from "../../../env";
import CommonToast from "../../../components/_utils/CommonToast";
import ICONS from "../../../assets/Icons";

const baseUrl = env.REACT_APP_BASE_URL;

export const reminderTxnView = createAsyncThunk(
  "reminderTxnView",
  async (values, { rejectWithValue }) => {
    const {
      search_string,
      page_no,
      page_size,
      start_date,
      end_date,
      min_amount,
      max_amount,
      remark,
    } = values;

    try {
      const response = await axiosInstance.get(`${baseUrl}/reminders`, {
        headers: {
          "Content-Type": "application/json",
          "X-Api-Key": sessionStorage.getItem("q2p_token"),
        },
        params: {
          page_no: page_no,
          page_size: page_size,
          search_string: search_string,
          start_date: start_date,
          end_date: end_date,
          min_amount: min_amount,
          max_amount: max_amount,
          remark: remark,
        },
      });
      return response?.data;
    } catch (error) {
      if (error?.response?.data?.error)
        CommonToast({
          text: error?.response?.data?.error,
          icon: ICONS?.ERROR_WHITE_CIRCLE,
          variant: "var(--error)",
          buttonNeeded: false,
        });
      return rejectWithValue(error);
    }
  },
);

export const reminderTxnDelete = createAsyncThunk(
  "reminderTxnDelete",
  async (id, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.delete(
        `${baseUrl}/reminders/${id}`,
        {
          headers: {
            "X-Api-Key": sessionStorage.getItem("q2p_token"),
            "Content-Type": "application/json",
          },
        },
      );

      const data = response.data;

      if (data?.status === false) {
        const errorMsg =
          data?.errors?.base?.[0] || "Failed to delete the reminder.";
        CommonToast({
          text: errorMsg,
          icon: ICONS?.ERROR_WHITE_CIRCLE,
          variant: "var(--error)",
          buttonNeeded: false,
        });
        return rejectWithValue(data);
      }

      return data;
    } catch (error) {
      const errorMessage =
        error?.response?.data?.errors?.base?.[0] ||
        error?.response?.data?.message ||
        "Something went wrong!";
      CommonToast({
        text: errorMessage,
        icon: ICONS?.ERROR_WHITE_CIRCLE,
        variant: "var(--error)",
        buttonNeeded: false,
      });
      console.error("Delete error:", error);
      return rejectWithValue(error);
    }
  },
);

export const reminderTxnCreate = createAsyncThunk(
  "reminderTxnCreate",
  async (values, { rejectWithValue }) => {
    const { amount, reminder_date, remark } = values?.paramsData;
    const requestBody = {
      reminder_date,
      amount,
      remark,
    };
    const config = {
      headers: {
        "Content-Type": "application/json",
        "X-Api-Key": sessionStorage.getItem("q2p_token"),
      },
    };

    try {
      if (values?.services?.includes("AReminder")) {
        const response = await axiosInstance.post(
          `${baseUrl}/reminders`,
          requestBody,
          config,
        );
        return response?.data;
      } else {
        values?.navigate("/feature_not_assigned");
      }
    } catch (error) {
      if (error?.response) {
        return rejectWithValue(error?.response?.data);
      } else {
        return rejectWithValue(error);
      }
    }
  },
);

const reminderTxnSlice = createSlice({
  name: "reminderTxnSlice",
  initialState: {
    reminderTxnViewData: null,
    reminderTxnViewLoading: null,
    reminderTxnViewError: null,

    reminderTxnDeleteData: null,
    reminderTxnDeleteLoading: null,
    reminderTxnDeleteError: null,

    reminderTxnCreateData: null,
    reminderTxnCreateLoading: null,
    reminderTxnCreateError: null,
    reminderCreated: false,
  },
  reducers: {
    clearCreateSuccess(state) {
      state.reminderCreated = false;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(reminderTxnView.pending, (state) => {
      state.reminderTxnViewLoading = true;
    });
    builder.addCase(reminderTxnView.fulfilled, (state, action) => {
      state.reminderTxnViewLoading = false;
      state.reminderTxnViewData = action?.payload;
    });
    builder.addCase(reminderTxnView.rejected, (state, action) => {
      state.reminderTxnViewLoading = false;
      state.reminderTxnViewData = false;
      state.reminderTxnViewError = action?.payload;
    });

    builder.addCase(reminderTxnDelete.pending, (state, action) => {
      state.reminderTxnDeleteLoading = true;
    });
    builder.addCase(reminderTxnDelete.fulfilled, (state, action) => {
      state.reminderTxnDeleteLoading = false;
      state.reminderTxnDeleteData = action.payload;
    });
    builder.addCase(reminderTxnDelete.rejected, (state, action) => {
      state.reminderTxnDeleteLoading = false;
      state.reminderTxnDeleteError = action?.payload;
      state.reminderTxnDeleteData = null;
    });

    builder.addCase(reminderTxnCreate.pending, (state) => {
      state.reminderTxnCreateLoading = true;
    });
    builder.addCase(reminderTxnCreate.fulfilled, (state, action) => {
      state.reminderTxnCreateLoading = false;
      state.reminderTxnCreateData = action?.payload;
      state.reminderCreated = true;
    });
    builder.addCase(reminderTxnCreate.rejected, (state, action) => {
      state.reminderTxnCreateLoading = false;
      state.reminderTxnCreateData = false;
      state.reminderTxnCreateError = action?.payload;
      CommonToast({
        text: action?.payload?.errors?.base?.[0],
        icon: ICONS?.ERROR_WHITE_CIRCLE,
        variant: "var(--error)",
        buttonNeeded: false,
      });
    });
  },
});

export const { clearCreateSuccess } = reminderTxnSlice.actions;
export default reminderTxnSlice.reducer;
